<template>
  <div class="events-manager events-grid">
    <grid
      ref="eventsGrid"
      :show-header="true"
      :row-class-fn="gridRowClassFn"
      loading-message="Loading events ..."
      :add-checkmarks="addCheckmarks"
      :edit-action-as-link="true"
      :list-url="overriddenListUrl || listUrl"
      grid-table-class="events-table"
      new-action-title="New event"
      :initial-filters="initialFilters"
      @meta-loaded="onMetaLoaded"
      @links-loaded="onLinksLoaded"
      @header-action="onHeaderAction"
    />

    <teleport
      to="#vue-portal-target"
      append
    >
      <xlsx-upload-modal
        ref="uploadModal"
        modal-title="Upload events"
        upload-description="Events can be uploaded from an XLSX (Excel) spreadsheet file"
        :upload-url="uploadUrl"
        :template-url="xlsxTemplateUrl"
        :post-data="additionalXlsxUploadPostData"
      >
        <template
          v-if="uploadOptions.showNcpSelector"
          #before-upload="{showUpload}"
        >
          <div
            v-if="showUpload"
            class="container"
          >
            <div class="row gx-2 mt-3 mb-3">
              <div class="col-12 mb-2">
                <label for="ncp-select" class="form-label required">Upload as NCP</label>
                <dropdown-field
                  v-model="uploadAsNcpId"
                  :items="uploadOptions.ncpItems"
                  placeholder="Select an NCP"
                />
              </div>
            </div>
          </div>
        </template>
      </xlsx-upload-modal>
      <modal-alert
        ref="modalAlert"
      />
      <modal-confirmation
        ref="confirmationModal"
        @confirmed="onConfirmed"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue'
import Grid from './grid.vue'
import XlsxUploadModal from './xlsx-upload-modal.vue'

import {getValueStrNullable, isNonEmptyString} from '../js-utils'
import type {ButtonAction, GridHeaderAction} from './types'
import ModalConfirmation from "@/js/vue-components/modal-confirmation.vue";
import i18next from "i18next";
import type {Confirmation} from "@/js/ospe-types";
import ModalAlert from "@/js/vue-components/modal-alert.vue";
import {default as DropdownField, type DropdownItem} from "@/js/vue-components/dropdown-field.vue";

interface UploadOptions {
  showNcpSelector: boolean
  ncpItems: DropdownItem[]
}

defineProps({
  listUrl: {default: null, type: String},
  initialFilters: {default: null, type: String},
  addCheckmarks: {default: false, type: Boolean}
})

const uploadUrl = ref<string | undefined>(undefined)
const xlsxTemplateUrl = ref<string | null>(null)
const overriddenListUrl = ref<string | null>(null)

const uploadOptions = ref<UploadOptions>({
  showNcpSelector: false,
  ncpItems: [],
})
const uploadAsNcpId = ref<string>("")

const eventsGrid = useTemplateRef<InstanceType<typeof Grid>>("eventsGrid")
const uploadModal = useTemplateRef<InstanceType<typeof XlsxUploadModal>>("uploadModal")
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>("modalAlert")
const confirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmation>>("confirmationModal")

const reload = (): void => {
  eventsGrid.value?.reload()
}

const onMetaLoaded = (meta: Record<string, any>): void => {
  uploadOptions.value.showNcpSelector = meta?.uploadEvents?.showNcpSelector ?? false
  uploadOptions.value.ncpItems = meta?.uploadEvents?.ncpItems ?? []
}

const onLinksLoaded = (links: Record<string, any>): void => {
  if (isNonEmptyString(links.upload)) {
    uploadUrl.value = links.upload
  }
  xlsxTemplateUrl.value = getValueStrNullable(links.xlsxTemplateUrl)
}

const onHeaderAction = (actionOrName: string | GridHeaderAction | ButtonAction): void => {
  if (typeof actionOrName === 'string') {
    return
  }

  const action = actionOrName
  if (action.name == 'upload-events') {
    showUploadDialog()
  } else if (action.name == 'confirm') {
    showOfferConfirmationDialog(action.url)
  }
}

const showUploadDialog = (): void => {
  uploadModal.value?.showModal()
}

const showOfferConfirmationDialog = (url: string): void => {
  const selectedItemIds = eventsGrid.value?.selectedItemIds || []
  if (selectedItemIds.length == 0) {
    const alertTitle = i18next.t('events_grid.confirm_event_hosting_offers.no_items_selected_alert.title')
    const alertMessage = i18next.t('events_grid.confirm_event_hosting_offers.no_items_selected_alert.message')
    modalAlert.value?.show(alertTitle, alertMessage, 'info')
    return
  }

  const selectedCount = selectedItemIds.length

  let confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: "",
    message: "",
    actionTitle: "",
    actionMessage: "",
    actionRequest: {
      method: 'post',
      params: {
        ids: selectedItemIds,
      },
      url
    }
  }

  confirmation.title = i18next.t('events_grid.confirm_event_hosting_offers.confirmation.title', {
    count: selectedCount
  })
  confirmation.message = i18next.t('events_grid.confirm_event_hosting_offers.confirmation.message', {
    count: selectedCount
  })
  confirmation.actionTitle = i18next.t('events_grid.confirm_event_hosting_offers.confirmation.action_title', {
    count: selectedCount
  })
  confirmation.actionMessage = i18next.t('events_grid.confirm_event_hosting_offers.confirmation.action_message', {
    count: selectedCount
  })

  confirmationModal.value?.show(confirmation)
}

const setFilterParams = (params: any): void => {
  eventsGrid.value?.setFilterParams(params)
}

const setListUrl = (url: string | null): void => {
  overriddenListUrl.value = url
}

const gridRowClassFn = (item: any): string | null => {
  return item.attributes?.is_published === false ? 'table-warning' : null
}

const onConfirmed = (): void => {
  eventsGrid.value?.reload()
}

const additionalXlsxUploadPostData = computed((): Record<string, string> | undefined => {
  if (isNonEmptyString(uploadAsNcpId.value)) {
    return {"ncp_id": uploadAsNcpId.value}
  }

  return undefined
})

defineExpose({
  reload,
  setFilterParams,
  setListUrl
})
</script>
